import { useState } from 'react'
import type { ReactElement } from 'react'
import { useAutoRefresh } from '../lib/use-auto-refresh.ts'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { withSessionRedirect } from '../lib/require-session.ts'
import type { AlertRow, AlertStatus } from '../lib/alerts-api.ts'
import {
  acknowledgeAlert,
  listAlerts,
  resolveAlert,
} from '../lib/alerts-api.ts'
import {
  Button,
  Callout,
  Chip,
  Code,
  DataTable,
  EmptyState,
  PageHeader,
  Toolbar,
} from '../components/ui/index.ts'
import type { Column, StatusTone } from '../components/ui/index.ts'

const statuses: AlertStatus[] = ['open', 'acknowledged', 'resolved']

export const Route = createFileRoute('/alerts')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { status?: AlertStatus } => {
    const status = search.status
    return status && statuses.includes(status as AlertStatus)
      ? { status: status as AlertStatus }
      : {}
  },
  loaderDeps: ({ search }) => ({ status: search.status }),
  loader: async ({ deps }) =>
    withSessionRedirect(async () => {
      const rows = await listAlerts(deps.status)
      return { rows }
    }),
  component: AlertsRoute,
})

/** Alerts are produced by the 5-minute sweep, so there is nothing to
 * gain from polling at session speed — this is slow enough to be cheap
 * and fast enough that an operator who just hit "Run checks now", or is
 * waiting on an agent, sees the result without reaching for reload. */
const REFRESH_MS = 30_000

const HTTP_URL = /(https?:\/\/[^\s]+)/g

/**
 * An alert's lifecycle, as status tones.
 *
 * `open` is `warn` rather than `danger`: the alert is the console asking
 * a human to look, and reserving `danger` for things that actually failed
 * is what keeps the red meaningful. `resolved` is `done`, which is
 * deliberately the quietest triple — a resolved queue should recede.
 */
const STATUS_TONES: Record<string, StatusTone> = {
  open: 'warn',
  acknowledged: 'pending',
  resolved: 'done',
}

/** `AlertRow.status` is typed `string` off the wire, not the three-value
 * union — a server raising a status this build has not heard of must not
 * render an uncoloured chip. */
function alertTone(status: string): StatusTone {
  return STATUS_TONES[status] ?? 'neutral'
}

/** Renders http(s) URLs in an alert message as links so an auth-needed
 * alert is one click from the login page. */
function LinkedAlertMessage({ text }: { text: string }): ReactElement {
  const parts = text.split(HTTP_URL)
  return (
    <>
      {parts.map((part, index) =>
        part.startsWith('http://') || part.startsWith('https://') ? (
          <a
            key={`${part}-${String(index)}`}
            href={part}
            target="_blank"
            rel="noreferrer"
            className="text-accent transition-colors hover:underline"
          >
            {part}
          </a>
        ) : (
          <span key={`${part}-${String(index)}`}>{part}</span>
        ),
      )}
    </>
  )
}

function AlertsRoute(): ReactElement {
  const { rows } = Route.useLoaderData()
  useAutoRefresh(REFRESH_MS)
  const search = Route.useSearch()
  const router = useRouter()
  const [error, setError] = useState<string | null>(null)

  async function refresh() {
    await router.invalidate()
  }

  async function handleAck(id: string) {
    setError(null)
    try {
      await acknowledgeAlert(id)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function handleResolve(id: string) {
    setError(null)
    try {
      await resolveAlert(id)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const columns: Column<AlertRow>[] = [
    {
      key: 'status',
      header: 'status',
      width: '8rem',
      render: (alert) => (
        <Chip tone={alertTone(alert.status)}>{alert.status}</Chip>
      ),
    },
    {
      key: 'kind',
      header: 'kind',
      width: '12rem',
      // The alert kind is a machine enum (`harness_offline`,
      // `memory_conflict`), not prose — monospace so the set is scannable
      // down the column.
      render: (alert) => <Code>{alert.kind}</Code>,
    },
    {
      key: 'message',
      header: 'message',
      render: (alert) =>
        alert.kind === 'memory_conflict' ? (
          // A conflict is only resolvable on /memory, so the message is
          // the link rather than growing a second actions column that is
          // empty for every other kind.
          <button
            type="button"
            className="rounded-sm text-left text-accent transition-colors hover:underline"
            onClick={() => void router.navigate({ to: '/memory' })}
          >
            {alert.message}
          </button>
        ) : (
          <LinkedAlertMessage text={alert.message} />
        ),
    },
    {
      key: 'actions',
      header: 'actions',
      align: 'right',
      width: '11rem',
      render: (alert) => (
        <span className="inline-flex gap-1">
          {alert.status === 'open' && (
            <Button size="sm" onClick={() => void handleAck(alert.id)}>
              Ack
            </Button>
          )}
          {alert.status !== 'resolved' && (
            <Button size="sm" onClick={() => void handleResolve(alert.id)}>
              Resolve
            </Button>
          )}
        </span>
      ),
    },
  ]

  function filterButton(label: string, status: AlertStatus | undefined) {
    const isActive = search.status === status
    return (
      <Button
        key={label}
        size="sm"
        variant={isActive ? 'primary' : 'ghost'}
        aria-pressed={isActive}
        onClick={() =>
          void router.navigate({
            to: '/alerts',
            search: status === undefined ? {} : { status },
          })
        }
      >
        {label}
      </Button>
    )
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Alerts"
        description="Raised by the five-minute sweep. Ack to say a human is on it; resolve to close it."
      />

      <Toolbar label="Alert status filter">
        {filterButton('all', undefined)}
        {statuses.map((status) => filterButton(status, status))}
      </Toolbar>

      {error !== null && (
        <div className="mb-2">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}

      <DataTable
        caption="Alerts"
        columns={columns}
        rows={rows}
        getRowKey={(alert) => alert.id}
        empty={
          <EmptyState
            headline={
              search.status === undefined
                ? 'No alerts'
                : `No ${search.status} alerts`
            }
            description="Alerts appear here when the sweep finds an offline harness, a stuck session or a memory conflict."
            action={
              search.status === undefined ? undefined : (
                <Button
                  onClick={() =>
                    void router.navigate({ to: '/alerts', search: {} })
                  }
                >
                  Show all alerts
                </Button>
              )
            }
          />
        }
      />
    </div>
  )
}
